import React, { forwardRef, ReactElement, ReactNode } from 'react'
import { FormatDateOptions, FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { Pressable, View } from 'react-native'

import { isPast, isToday } from 'date-fns'
import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import {
  getBookedAddressByStreet,
  getSelectedOnsiteAddress,
} from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { BOOKING_CHANNEL_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/calendar/types'
import {
  TREATMENT_OPTIONS_FROM_FRONT_END,
  TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND,
  TREATMENT_OPTIONS_TYPE,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/treatmentOptions'
import { assertUnreachable } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/commonUtils'
import { isWithin24Hours } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/dateUtils'
import { ProgramDisplayLabels } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/messages'
import {
  getProgramDisplayLabel,
  getProgramNameFromId,
} from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import {
  formatAddressForDisplay,
  getAddressCoordinates,
  getDistanceToProvider,
} from '@lyrahealth-inc/shared-app-logic/src/features/providers/utils'
import { SessionCostEstimateDisplayState } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/constants'
import {
  Appointment,
  CalendarCardAppointment,
} from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { CostInfo } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import { CalendarCardActionMenu, sessionActionMessages } from './CalendarCardActionMenu'
import { ButtonSize } from '../../atoms/baseButton/BaseButton'
import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { Display } from '../../atoms/display/Display'
import { AddToCalendarIcon } from '../../atoms/icons/AddToCalendarIcon'
import { Avatar, avatarAltTextGeneric, AvatarDetails } from '../../atoms/icons/Avatar'
import { DirectionIcon } from '../../atoms/icons/DirectionIcon'
import { SessionListPlayIcon } from '../../atoms/icons/SessionListPlayIcon'
import { TextBadge } from '../../atoms/icons/TextBadge'
import { Link } from '../../atoms/link/Link'
import { ManageSessionLink } from '../../atoms/link/ManageSessionLink'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { Title } from '../../atoms/title/Title'
import { TruncatedText } from '../../atoms/truncate/TruncatedText'
import { BadgeVariant } from '../../badge/constants'
import { IS_WEB } from '../../constants'
import { useMediaQuerySize } from '../../hooks/useMediaQuerySize'
import { getAuroraAwareBackgroundColor } from '../../styles/commonStyles'
import { DisplaySize, TextType, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { openHyperlinkForDirectionsToAddress, tID } from '../../utils/utils'

export type CalendarCardProps = {
  id: number
  onPress?: () => void
  avatarDetails?: AvatarDetails
  title: string
  session: Appointment
  subtitle?: string | ReactElement | ReactNode
  icon?: ReactElement<View>
  isMissed?: boolean
  addToCalendarLink?: (appointmentId: { appointmentId: number }) => void
  handleReschedulePress?: (appointment: Appointment) => void
  handleCancelPress?: (appointment: Appointment) => void
  providerIcon?: ReactElement
  viewSessionRecording?: (sessionID: string) => void
  showManageSessionButton?: boolean // show "Manage session" button in native mobile, default to true
  disallowSessionUpdate?: boolean // do not allow cancel or reschedule a session by user
  testId?: string
  providerID?: string
  childFirstName?: string
  isDAEngageProvider?: boolean
  isSessionsCostEstimateEnabled?: boolean
  providerFirstName?: string
  withOnlyBottomBorder?: boolean
  navigateToRescheduleSession?: (appointment: Appointment) => void
  onCancelSessionPress?: (appointment: Appointment) => void
  costInfo?: CostInfo
  costEstimateDisplayState?: SessionCostEstimateDisplayState
  handleCostInfoPress?: (costInfo: CostInfo) => void
  useKilometers?: boolean
}

const CardContainer = styled.View<{ theme: ThemeType; withOnlyBottomBorder?: boolean }>(
  ({ theme, withOnlyBottomBorder }) => ({
    display: 'flex',
    flexDirection: 'column',
    paddingVertical: theme.spacing['8px'],
    padding: theme.spacing['16px'],
    marginBottom: theme.spacing['8px'],
    alignItems: 'center',
    minHeight: '80px',
    backgroundColor:
      withOnlyBottomBorder && !theme.isAuroraTheme
        ? 'transparent'
        : getAuroraAwareBackgroundColor(
            theme.isAuroraTheme,
            theme.colors.backgroundPrimary,
            theme.colors.cardBackgroundPrimary,
          ),
    borderWidth: withOnlyBottomBorder || theme.isAuroraTheme ? '0' : '1px',
    borderColor: theme.colors.borderDefault,
    borderRadius: theme.breakpoints.isMinWidthLaptop ? theme.spacing['16px'] : theme.spacing['12px'],
    ...(withOnlyBottomBorder && !theme.isAuroraTheme && { borderBottomWidth: '1px', borderRadius: 0 }),
  }),
)

const CardMainContents = styled(View)({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
})

const AvatarContainer = styled.View({
  position: 'relative',
})

const SessionDateTimeContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  fontSize: '14px',
  display: 'flex',
  marginTop: '4px',
})

const TitleTextContainer = styled.View<{ theme: ThemeType; isSessionsCostEstimateEnabled: boolean }>(
  ({ theme, isSessionsCostEstimateEnabled }) => ({
    marginLeft: 15,
    flexShrink: 1,
    paddingRight: theme.breakpoints.isMinWidthLaptop ? 16 : isSessionsCostEstimateEnabled ? 0 : 8,
  }),
)

const Icon = styled.View({
  position: 'absolute',
  right: '-4px',
  bottom: '-4px',
})

const DateBlockContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  marginLeft: 'auto',
  height: theme.breakpoints.isMinWidthLaptop ? '56px' : '48px',
  width: theme.breakpoints.isMinWidthLaptop ? '56px' : '48px',
  backgroundColor: theme.colors.backgroundSection,
  borderRadius: '5px',
  overflow: 'visible',
  color: theme.colors.textSecondary,
}))

const AppointmentActionsContainer = styled(View)<{ theme: ThemeType; providerIconNullish: boolean }>(({
  theme,
  providerIconNullish,
}) => {
  return {
    color: theme.colors.textActive,
    display: !providerIconNullish || theme.breakpoints.isMinWidthLaptop ? 'flex' : 'none',
    flexDirection: 'row',
    width: '100%',
  }
})

const ManageSessionButtonContainer = styled(PrimaryButton)(({ theme }) => ({
  display: !theme.breakpoints.isMinWidthLaptop ? 'flex' : 'none',
  width: '100%',
  marginTop: theme.spacing['16px'],
}))

const DisplayContainer = styled(Display)({
  ...(IS_WEB && {
    lineHeight: '100%',
  }),
})

const SubHeadContainer = styled(Title)({
  ...(IS_WEB && {
    lineHeight: '100%',
  }),
})

const ManageSessionLinkContainer = styled(View)<{ theme: ThemeType; first?: boolean }>(({ theme, first }) => {
  return {
    height: theme.spacing['24px'],
    display: 'flex',
    justifyContent: 'center',
    marginLeft: first ? `-${theme.spacing['8px']}` : '0px',
    paddingLeft: first ? '0px' : theme.spacing['8px'],
    paddingRight: theme.spacing['8px'],
    borderLeftWidth: first ? '0' : '1px',
    borderLeftColor: first ? '0' : theme.colors.borderDefault,
    marginTop: theme.spacing['16px'],
  }
})

const RecordingUnavailableContainer = styled(View)<{ theme: ThemeType; first?: boolean }>(({ theme, first }) => {
  return {
    height: theme.spacing['24px'],
    display: 'flex',
    justifyContent: 'center',
    paddingLeft: first ? 0 : theme.spacing['8px'],
    paddingRight: theme.spacing['8px'],
    borderLeftWidth: '0',
    marginTop: theme.spacing['16px'],
    color: theme.colors.textSecondary,
  }
})

const ProviderIconContainer = styled.View({
  alignSelf: 'flex-start',
})

const TodayContainer = styled.View({
  fontSize: '14px',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
})

const SessionAttendanceContainer = styled.View({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexShrink: 0,
})

const ActionMenuContainer = styled.View({
  marginLeft: 'auto',
})

const ChildBadgeContainer = styled.View({
  display: 'flex',
  flexDirection: 'row',
})

const LocationSectionDivider = styled(View)<{ theme: ThemeType }>(({ theme }) => ({
  height: '1px',
  backgroundColor: theme.colors.borderDefault,
  marginTop: theme.spacing['16px'],
  width: '100%',
}))

const LocationSection = styled(View)<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  marginTop: theme.spacing['12px'],
  width: '100%',
}))

const LocationTextContainer = styled(View)({
  flex: 1,
})

const DirectionsButton = styled(Pressable)<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.spacing['8px'],
  alignSelf: 'center',
}))

/**
 * Represents a single session as a card which contains
 * an avatar, title name, session info, and calendar icon
 */
let CalendarCard = forwardRef<unknown, CalendarCardProps>(
  (
    {
      id,
      onPress,
      avatarDetails,
      title,
      subtitle,
      icon,
      isMissed,
      handleReschedulePress,
      handleCancelPress,
      providerIcon,
      viewSessionRecording,
      addToCalendarLink,
      session,
      showManageSessionButton = true,
      testId = '',
      providerID,
      childFirstName,
      disallowSessionUpdate,
      isDAEngageProvider,
      isSessionsCostEstimateEnabled = false,
      providerFirstName = '',
      withOnlyBottomBorder = false,
      navigateToRescheduleSession,
      onCancelSessionPress,
      costInfo,
      costEstimateDisplayState,
      handleCostInfoPress,
      useKilometers = false,
    },
    _,
  ) => {
    const { isMinWidthLaptop } = useMediaQuerySize()
    const { formatMessage, formatNumber } = useIntl()
    const { colors } = useTheme()
    const { appointmentTodayText } = colors.components.calendarCard
    const onCardPress = onPress || noop
    const intl = useIntl()
    const dateTime = getDateFromAppointment(session)
    const isAppointmentToday = isToday(dateTime)
    const isInPast = isPast(dateTime)
    const shouldHideTime = session.bookingChannel === BOOKING_CHANNEL_TYPE.PP_API_AUTO_APPT_CREATION && isInPast
    const day = intl.formatTime(dateTime, {
      month: 'long',
      day: 'numeric',
      timeZone: session?.timeZone ?? '',
      timeZoneName: 'short',
    })

    const dateFormatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }

    const timeFormatOptions = {
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short',
    }
    const todayFormatted = (
      <TodayContainer>
        <FormattedMessage defaultMessage='Today' description='current day' />
      </TodayContainer>
    )

    const programName = getProgramNameFromId(session?.programId)
    const treatmentType = session?.treatmentType as TREATMENT_OPTIONS_TYPE | undefined
    const sessionNumber = session?.sessionNumber === 0 ? 1 : session?.sessionNumber
    const formattedDate = intl.formatDate(dateTime, dateFormatOptions as FormatDateOptions)
    const formattedTime = intl.formatDate(dateTime, timeFormatOptions as FormatDateOptions)
    const isCancelled = session?.appointmentStatus === 'canceled'
    const isTooLateToReschedule =
      !isInPast && session && session.appointmentStatus !== 'canceled' && isWithin24Hours(session)
    if (!session) {
      return null
    }

    const childBadgeText = childFirstName
      ? formatMessage(
          {
            defaultMessage: 'For {childFirstName}',
            description: 'Text to indicate the child the session is for',
          },
          { childFirstName },
        )
      : null

    const showActionMenu = isSessionsCostEstimateEnabled && !isInPast && !isCancelled
    const showDateBlock = !isSessionsCostEstimateEnabled
    const hasCostEstimateDisplay = costEstimateDisplayState != null

    const isInPersonAppointment =
      session.meetingFormat === MEETING_FORMATS.IN_PERSON ||
      session.meetingFormat === MEETING_FORMATS.IN_PERSON_UNDERSCORE
    const appointmentLocationId = session.locationId ? Number(session.locationId) : undefined
    const appointmentAddress =
      getSelectedOnsiteAddress(appointmentLocationId, session.provider?.addresses) ??
      getBookedAddressByStreet(session.address, session.provider?.addresses)
    const locationAddress = appointmentAddress ?? session.provider?.nearestAddress
    const showLocationSection = isInPersonAppointment && !isInPast && !isCancelled && !!locationAddress
    const displayAddress = locationAddress ? formatAddressForDisplay(locationAddress) : ''
    const { lat: addressLat, lon: addressLon } = getAddressCoordinates(locationAddress)
    const locationName = session.provider?.clinic_name ?? locationAddress?.name
    const nearestDistanceValue = session.provider?.nearestDistance
    const distanceDisplay =
      nearestDistanceValue != null && nearestDistanceValue > 0 && !appointmentAddress
        ? getDistanceToProvider({ distance: nearestDistanceValue, useKilometers })
        : undefined

    return (
      <CardContainer
        testID={tID(`CalendarCard${testId ? '-' + testId : ''}`)}
        key={id}
        accessibilityLabel={`${title}${subtitle ? `, ${subtitle}` : ''}, on ${day}`}
        withOnlyBottomBorder={isSessionsCostEstimateEnabled && withOnlyBottomBorder}
      >
        <CardMainContents testID={tID('CalendarCard-information-block')}>
          {avatarDetails && (
            <AvatarContainer>
              <Avatar
                details={avatarDetails}
                accessibilityLabel={formatMessage(avatarAltTextGeneric, { name: avatarDetails.displayName })}
                size={isMinWidthLaptop ? 60 : 48}
                providerID={providerID}
              />

              <Icon testID={tID(`CalendarCard-modality-icon-${session?.meetingFormat}`)}>{icon}</Icon>
            </AvatarContainer>
          )}
          {providerIcon && <ProviderIconContainer>{providerIcon}</ProviderIconContainer>}

          {/* Title */}
          <TitleTextContainer
            testID={tID('CalendarCard-title')}
            importantForAccessibility='no-hide-descendants'
            isSessionsCostEstimateEnabled={isSessionsCostEstimateEnabled}
          >
            {isSessionsCostEstimateEnabled ? (
              <BodyText testID={tID('CalendarCard-appointment-time')}>
                {childBadgeText && (
                  <ChildBadgeContainer>
                    <TextBadge
                      testID={tID('CalendarCard-childBadge')}
                      text={childBadgeText}
                      variant={BadgeVariant.INFO_SECONDARY}
                      preserveCase
                      bold
                    />
                  </ChildBadgeContainer>
                )}
                <FormattedMessage
                  defaultMessage='{date}{spacer}{time}{endSpacer}'
                  description='Date and time of the appointment'
                  values={{
                    date: formattedDate,
                    spacer: shouldHideTime ? '' : ' • ',
                    time: shouldHideTime ? '' : formattedTime,
                    endSpacer: isAppointmentToday ? ' • ' : '',
                  }}
                />
                {isAppointmentToday && (
                  <BodyText
                    text={todayFormatted}
                    color={appointmentTodayText.text}
                    size={BodyTextSize.CAPTION}
                    testID={tID('CalendarCard-Today')}
                  />
                )}
              </BodyText>
            ) : (
              <TruncatedText
                fontColor={colors.textPrimary}
                text={title}
                textType={TextType.BODY}
                textSize={BodyTextSize.DEFAULT}
                maxNumberOfLines={1}
                noMargin
                textDecorator={
                  childBadgeText ? (
                    <TextBadge
                      testID={tID('CalendarCard-childBadge')}
                      text={childBadgeText}
                      variant={BadgeVariant.INFO_SECONDARY}
                      preserveCase
                      bold
                    />
                  ) : undefined
                }
              />
            )}

            {/* Subtitle */}
            <BodyText size={BodyTextSize.SMALL} color={colors.textSecondary} testID={tID('CalendarCard-subtitle')}>
              {isCancelled && !isSessionsCostEstimateEnabled ? (
                !providerIcon ? (
                  <>
                    <BodyText
                      color={colors.textSecondary}
                      // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                      text={getCalendarCardProgramDisplayName(intl, programName, treatmentType) + ' • '}
                      size={BodyTextSize.SMALL}
                      testID={tID('CalendarCard-programName')}
                    />
                    <BodyText
                      text={
                        <FormattedMessage
                          defaultMessage={`Canceled`}
                          description='Canceled or anulled or no longer valid'
                        />
                      }
                      color={colors.textWarning}
                      size={BodyTextSize.SMALL}
                      testID={tID('CalendarCard-cancelled')}
                    />
                  </>
                ) : (
                  <BodyText text={subtitle} size={BodyTextSize.SMALL} color={colors.textSecondary} />
                )
              ) : (
                <BodyText
                  text={
                    subtitle && !isSessionsCostEstimateEnabled
                      ? subtitle
                      : formatCalendarCardSubtitle(
                          intl,
                          programName,
                          treatmentType,
                          sessionNumber,
                          isSessionsCostEstimateEnabled,
                          providerFirstName,
                        )
                  }
                  size={isSessionsCostEstimateEnabled ? BodyTextSize.CAPTION : BodyTextSize.SMALL}
                  color={colors.textSecondary}
                />
              )}
            </BodyText>

            {/* Session attendance or Session date and time */}
            {isSessionsCostEstimateEnabled ? (
              <SessionAttendanceContainer>
                <BodyText
                  color={colors.textSecondary}
                  size={BodyTextSize.CAPTION}
                  testID={tID('CalendarCard-programName')}
                >
                  <FormattedMessage
                    defaultMessage={`Session {sessionNumber}{spacer}`}
                    description='Label showing the session number'
                    values={{
                      sessionNumber,
                      spacer: ' • ',
                    }}
                  />
                </BodyText>

                {(isInPast || isCancelled) && (
                  <BodyText
                    text={
                      isCancelled ? (
                        <FormattedMessage
                          defaultMessage={`Canceled`}
                          description='Label showing the session was canceled'
                        />
                      ) : (
                        <FormattedMessage
                          defaultMessage={`Attended`}
                          description='Label showing the session was attended'
                        />
                      )
                    }
                    color={isCancelled ? colors.textWarning : colors.textSecondary}
                    size={BodyTextSize.CAPTION}
                    testID={tID(isCancelled ? 'CalendarCard-cancelled' : 'CalendarCard-attended')}
                  />
                )}

                {!isInPast && !isCancelled && (
                  <BodyText
                    text={
                      <FormattedMessage
                        defaultMessage={`{duration} mins{spacer}`}
                        description='Label showing the session duration'
                        values={{
                          duration: session?.appointmentDuration,
                          spacer: hasCostEstimateDisplay ? ' • ' : '',
                        }}
                      />
                    }
                    size={BodyTextSize.CAPTION}
                  />
                )}
                {!isInPast && !isCancelled && (
                  <>
                    {costEstimateDisplayState === SessionCostEstimateDisplayState.AVAILABLE &&
                      costInfo?.client_session_cost != null && (
                        <>
                          <Link
                            text={formatMessage(
                              {
                                defaultMessage: '{cost} estimated',
                                description: 'Link to view cost details',
                              },
                              {
                                cost: formatNumber(costInfo.client_session_cost as number, {
                                  style: 'currency',
                                  currency: 'USD',
                                  maximumFractionDigits: 0,
                                }),
                              },
                            )}
                            size={BodyTextSize.CAPTION}
                            color={colors.linkDefault}
                            onPress={() => handleCostInfoPress?.(costInfo)}
                            underline
                            testID={tID('CalendarCard-cost-estimate-link')}
                          />
                          <BodyText
                            text={formatMessage({
                              defaultMessage: '*',
                              description: 'Asterisk indicating disclaimer for cost estimate',
                            })}
                            size={BodyTextSize.CAPTION}
                          />
                        </>
                      )}
                    {costEstimateDisplayState === SessionCostEstimateDisplayState.UNAVAILABLE && (
                      <BodyText
                        text={formatMessage({
                          defaultMessage: 'Cost unavailable',
                          description: 'Message shown when cost estimate cannot be retrieved',
                        })}
                        size={BodyTextSize.CAPTION}
                        color={colors.textInactive}
                        testID={tID('CalendarCard-cost-estimate-error-label')}
                      />
                    )}
                    {costEstimateDisplayState === SessionCostEstimateDisplayState.LOADING && (
                      <BodyText
                        text={formatMessage({
                          defaultMessage: 'Cost estimate loading',
                          description: 'Message shown when cost estimate is loading',
                        })}
                        size={BodyTextSize.CAPTION}
                        color={colors.textInactive}
                        testID={tID('CalendarCard-cost-estimate-loading-label')}
                      />
                    )}
                    {costEstimateDisplayState === SessionCostEstimateDisplayState.DEFERRED && (
                      <BodyText
                        text={formatMessage({
                          defaultMessage: 'Estimate available closer to care',
                          description:
                            'Message explaining that an estimate for a later scheduled session will be shown closer to the appointment',
                        })}
                        size={BodyTextSize.CAPTION}
                        color={colors.textInactive}
                        testID={tID('CalendarCard-cost-estimate-deferred-label')}
                      />
                    )}
                  </>
                )}
              </SessionAttendanceContainer>
            ) : (
              <SessionDateTimeContainer>
                <BodyText size={BodyTextSize.SMALL} testID={tID('CalendarCard-appointment-time')}>
                  <FormattedMessage
                    defaultMessage='{date}{spacer}{time}{endSpacer}'
                    description='Date and time of the appointment'
                    values={{
                      date: formattedDate,
                      spacer: shouldHideTime ? '' : ' • ',
                      time: shouldHideTime ? '' : formattedTime,
                      endSpacer: isAppointmentToday ? ' • ' : '',
                    }}
                  />
                  {isAppointmentToday && (
                    <BodyText
                      text={todayFormatted}
                      color={appointmentTodayText.text}
                      size={BodyTextSize.CAPTION}
                      testID={tID('CalendarCard-Today')}
                    />
                  )}
                </BodyText>
              </SessionDateTimeContainer>
            )}
          </TitleTextContainer>

          {/* 3 Buttons Menu or Date Block */}
          {showActionMenu ? (
            <ActionMenuContainer>
              <CalendarCardActionMenu
                addToCalendarLink={addToCalendarLink}
                disallowSessionUpdate={disallowSessionUpdate}
                handleCancelPress={handleCancelPress}
                handleReschedulePress={handleReschedulePress}
                id={id}
                isTooLateToReschedule={isTooLateToReschedule}
                navigateToRescheduleSession={navigateToRescheduleSession}
                onCancelSessionPress={onCancelSessionPress}
                session={session}
              />
            </ActionMenuContainer>
          ) : showDateBlock ? (
            <DateBlockContainer testID={tID('CalendarCard-date-block')} importantForAccessibility='no-hide-descendants'>
              <DisplayContainer
                size={DisplaySize.EYEBROW}
                text={intl
                  .formatDate(dateTime, {
                    month: 'short',
                  })
                  .toUpperCase()}
              />
              <SubHeadContainer
                size={TitleSize.MEDIUM}
                text={intl.formatDate(dateTime, {
                  day: 'numeric',
                })}
                level='4'
              />
            </DateBlockContainer>
          ) : null}
        </CardMainContents>

        {!isInPast &&
          session?.appointmentStatus !== 'canceled' &&
          !providerIcon &&
          showManageSessionButton &&
          !disallowSessionUpdate &&
          !isSessionsCostEstimateEnabled && (
            <ManageSessionButtonContainer
              fullWidth={true}
              onPress={onCardPress}
              size={ButtonSize.SMALL}
              testID={tID('CalendarCard-manage-button')}
              text={
                <FormattedMessage defaultMessage='Manage session' description='Label of button that submits the form' />
              }
            />
          )}

        {!isSessionsCostEstimateEnabled && !isMissed && (
          <AppointmentActionsContainer
            providerIconNullish={!providerIcon}
            testID={tID('CalendarCard-actions-container')}
          >
            {!isDAEngageProvider && viewSessionRecording && session && session.videoRecording && (
              <ManageSessionLinkContainer first={true} testID={tID('CalendarCard-watch-recording')}>
                <ManageSessionLink
                  icon={<SessionListPlayIcon />}
                  linkText={
                    <BodyText
                      color={colors.linkDefault}
                      size={BodyTextSize.DEFAULT}
                      text={
                        <FormattedMessage
                          defaultMessage='Watch recording'
                          description='Watch the recorded appointment'
                        />
                      }
                    />
                  }
                  onPress={() => {
                    viewSessionRecording(session.videoRecording as string)
                  }}
                />
              </ManageSessionLinkContainer>
            )}
            {!isDAEngageProvider && isInPast && session && !session.videoRecording && providerIcon && (
              <RecordingUnavailableContainer first={true} testID={tID('CalendarCard-watch-recording-not-available')}>
                <BodyText
                  color={colors.textActive}
                  size={BodyTextSize.DEFAULT}
                  text={
                    <FormattedMessage
                      defaultMessage='Recording unavailable'
                      description='Recorded meeting is not available to watch'
                    />
                  }
                />
              </RecordingUnavailableContainer>
            )}
            {addToCalendarLink && session?.appointmentStatus !== 'canceled' && (
              <ManageSessionLinkContainer first={true} testID={tID('CalendarCard-add-to-calendar')}>
                <ManageSessionLink
                  icon={<AddToCalendarIcon />}
                  linkText={
                    <BodyText
                      color={colors.linkDefault}
                      size={BodyTextSize.DEFAULT}
                      text={
                        <FormattedMessage defaultMessage='Add to calendar' description='Add appointment to calendar' />
                      }
                    />
                  }
                  onPress={() => {
                    addToCalendarLink({ appointmentId: id })
                  }}
                />
              </ManageSessionLinkContainer>
            )}
            {!isTooLateToReschedule &&
              handleReschedulePress &&
              session?.appointmentStatus !== 'canceled' &&
              !disallowSessionUpdate && (
                <ManageSessionLinkContainer first={!addToCalendarLink} testID={tID('CalendarCard-reschedule')}>
                  <ManageSessionLink
                    accessibilityLabel={formatMessage(sessionActionMessages.rescheduleSession)}
                    linkText={
                      <BodyText
                        color={colors.linkDefault}
                        size={BodyTextSize.DEFAULT}
                        text={
                          <FormattedMessage
                            defaultMessage='Reschedule'
                            description='Change the schedule of the appointment'
                          />
                        }
                      />
                    }
                    onPress={() => handleReschedulePress(session)}
                  />
                </ManageSessionLinkContainer>
              )}
            {handleCancelPress && session?.appointmentStatus !== 'canceled' && !disallowSessionUpdate && (
              <ManageSessionLinkContainer
                first={isTooLateToReschedule && handleReschedulePress !== undefined && providerIcon !== undefined}
                testID={tID('CalendarCard-cancel')}
              >
                <ManageSessionLink
                  accessibilityLabel={formatMessage(sessionActionMessages.cancelSession)}
                  linkText={
                    <BodyText
                      color={colors.textActive}
                      size={BodyTextSize.DEFAULT}
                      text={<FormattedMessage defaultMessage='Cancel session' description='Cancel the appointment' />}
                    />
                  }
                  onPress={() => {
                    handleCancelPress(session as CalendarCardAppointment)
                  }}
                />
              </ManageSessionLinkContainer>
            )}
          </AppointmentActionsContainer>
        )}
        {showLocationSection && (
          <>
            <LocationSectionDivider testID={tID('CalendarCard-location-divider')} />
            <LocationSection testID={tID('CalendarCard-location-section')}>
              <LocationTextContainer>
                {locationName && (
                  <BodyText
                    text={locationName}
                    size={BodyTextSize.DEFAULT}
                    testID={tID('CalendarCard-location-name')}
                  />
                )}
                {displayAddress && (
                  <Link
                    text={displayAddress}
                    size={BodyTextSize.SMALL}
                    color={colors.linkDefault}
                    onPress={() => openHyperlinkForDirectionsToAddress(displayAddress, addressLat, addressLon)}
                    underline
                    testID={tID('CalendarCard-location-address')}
                  />
                )}
                {distanceDisplay && (
                  <BodyText
                    text={formatMessage(
                      {
                        defaultMessage: '{distance} away',
                        description: 'Distance to in-person appointment location',
                      },
                      { distance: `${distanceDisplay.value} ${distanceDisplay.unit}` },
                    )}
                    size={BodyTextSize.SMALL}
                    color={colors.textSecondary}
                    testID={tID('CalendarCard-location-distance')}
                  />
                )}
              </LocationTextContainer>
              <DirectionsButton
                onPress={() => openHyperlinkForDirectionsToAddress(displayAddress, addressLat, addressLon)}
                testID={tID('CalendarCard-directions-button')}
                accessibilityLabel={formatMessage({
                  defaultMessage: 'Get directions',
                  description: 'Accessibility label for directions button on appointment card',
                })}
                accessibilityRole='button'
                style={{ marginLeft: 8 }}
              >
                <DirectionIcon />
              </DirectionsButton>
            </LocationSection>
          </>
        )}
      </CardContainer>
    )
  },
)

function getCalendarCardProgramDisplayName(
  intl: IntlShape,
  programName: ProgramNames | undefined,
  treatmentType: TREATMENT_OPTIONS_TYPE | undefined,
): string | undefined {
  if (programName) {
    return getProgramDisplayLabel(intl, programName, 'careType')
  }

  if (!treatmentType) {
    return undefined
  }

  switch (treatmentType) {
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.COACHING:
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.LIVE_MESSAGING_COACHING:
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.COACHING_FOR_PARENTS:
      return intl.formatMessage(ProgramDisplayLabels.coaching)

    case TREATMENT_OPTIONS_FROM_FRONT_END.DIALECTICAL_BEHAVIOR_THERAPY:
    case TREATMENT_OPTIONS_FROM_FRONT_END.DIALECTICAL_BEHAVIOR_THERAPY_TEENS:
    case TREATMENT_OPTIONS_FROM_FRONT_END.ONSITE_THERAPY:
    case TREATMENT_OPTIONS_FROM_FRONT_END.CUSTOMER_PROVIDED_PROGRAM:
    case TREATMENT_OPTIONS_FROM_FRONT_END.BLENDED_CARE_THERAPY:
    case TREATMENT_OPTIONS_FROM_FRONT_END.INDIVIDUAL_THERAPY_CHILD_ICAS_PHONE:
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.INDIVIDUAL_THERAPY:
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.INDIVIDUAL_THERAPY_ICAS_PHONE:
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.FAMILY_THERAPY:
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.COUPLES_THERAPY:
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.FAMILY_THERAPY_CHILD:
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.INDIVIDUAL_THERAPY_CHILD:
      return intl.formatMessage(ProgramDisplayLabels.therapy)

    case TREATMENT_OPTIONS_FROM_FRONT_END.CONTROLLED_MEDICATION:
    case TREATMENT_OPTIONS_FROM_FRONT_END.MEDICATION_CONSULTATION:
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.BLENDED_CARE_MEDS:
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.DIRECT_ACCESS_MEDS:
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.DIRECT_ACCESS_MEDS_CHILD:
      return intl.formatMessage(ProgramDisplayLabels.medicationManagement)
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.SELF_CARE_APPS:
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.GUIDED_SELF_CARE:
      return intl.formatMessage(ProgramDisplayLabels.guidedSelfCare)
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.ALCOHOL_USE_DISORDER:
      return intl.formatMessage(ProgramDisplayLabels.renew)
    case TREATMENT_OPTIONS_FROM_FRONT_END.CLINICAL_LEAVE_EVALUATION:
      return intl.formatMessage(ProgramDisplayLabels.clinicalLeaveEvaluation)
    case TREATMENT_OPTIONS_FROM_FRONT_END.SUBSTANCE_USE_EVALUATION:
      return intl.formatMessage(ProgramDisplayLabels.substanceUseEvaluation)
    case TREATMENT_OPTIONS_FROM_FRONT_END.NEURODIVERSITY_SKILLS_COACHING:
      return intl.formatMessage(ProgramDisplayLabels.neurodiversitySkillsCoaching)
    case TREATMENT_OPTIONS_FROM_FRONT_END.CAREGIVER_TRAINING_FOR_NEURO:
      return intl.formatMessage(ProgramDisplayLabels.caregiverTrainingForNeuro)
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.MANAGER_COACHING:
      return intl.formatMessage(ProgramDisplayLabels.managerCoaching)
    case TREATMENT_OPTIONS_FROM_FRONT_END.PEDIATRIC_VIRTUAL_INTENSIVE_OUTPATIENT_PROGRAM:
    case TREATMENT_OPTIONS_FROM_FRONT_END.PEDIATRIC_VIRTUAL_INTENSIVE_OUTPATIENT_PROGRAM_CHILD:
    case TREATMENT_OPTIONS_FROM_FRONT_END.PEDIATRIC_BEHAVIORAL_HEALTH_URGENT_CARE:
    case TREATMENT_OPTIONS_FROM_FRONT_END.PEDIATRIC_BEHAVIORAL_HEALTH_URGENT_CARE_CHILD:
    case TREATMENT_OPTIONS_FROM_FRONT_END.PEDIATRIC_FACILITIES_COORDINATION:
    case TREATMENT_OPTIONS_FROM_FRONT_END.PEDIATRIC_FACILITIES_COORDINATION_CHILD:
    case TREATMENT_OPTIONS_FROM_FRONT_END.PERFORMANCE_COACHING:
    case TREATMENT_OPTIONS_FROM_FRONT_END.NEUROPSYCHOLOGICAL_EVALUATIONS:
    case TREATMENT_OPTIONS_FROM_FRONT_END.NEUROPSYCHOLOGICAL_EVALUATIONS_CHILD:
    case TREATMENT_OPTIONS_FROM_FRONT_END.SUPPORT_FOR_NEURODIVERSITY:
    case TREATMENT_OPTIONS_FROM_FRONT_END.SUPPORT_FOR_NEURODIVERSITY_CHILD:
      return undefined
    default:
      assertUnreachable(treatmentType, 'getCalendarCardProgramDisplayName: Unknown treatment type: ' + treatmentType)
      return undefined
  }
}

function formatCalendarCardSubtitle(
  intl: IntlShape,
  programName: ProgramNames | undefined,
  treatmentType: TREATMENT_OPTIONS_TYPE | undefined,
  sessionNumber: number,
  isSessionsCostEstimateEnabled: boolean,
  providerFirstName: string,
): string {
  const programNameText = getCalendarCardProgramDisplayName(intl, programName, treatmentType)
  const sessionNumberText = intl.formatMessage(
    { defaultMessage: 'Session {sessionNumber}', description: 'Label showing the session number' },
    { sessionNumber },
  )

  if (isSessionsCostEstimateEnabled) {
    return intl.formatMessage(
      {
        defaultMessage:
          '{programNameText, select, undefined {Session} other {{programNameText}}} with {providerFirstName}',
        description: 'Subtitle showing the program name (or "Session" if unavailable) with the provider first name',
      },
      { programNameText: programNameText || 'undefined', providerFirstName },
    )
  } else {
    return programNameText ? `${programNameText} • ${sessionNumberText}` : sessionNumberText
  }
}

CalendarCard = React.memo(CalendarCard)

export { CalendarCard }
